"use client";

import Image from "next/image"
import Link from "next/link"
import CTA from "@/components/home/cta"
import { Tab } from '@headlessui/react'
import { useEffect, useState } from 'react'

import Header from "@/components/layout/header";
import Footer from "@/components/layout/footer";
import RelatedLinks from "@/components/shared/related-links";
import FAQSection from "@/components/shared/faq-section";
import { inventoryFaqs } from "./faqs";

const relatedLinks = [
  {
    href: "/order-management-platform",
    title: "Order Management Platform",
    description: "End-to-end ordering — integrates with RFID inventory for automated replenishment.",
  },
  {
    href: "/linen-management-system",
    title: "Linen Management System",
    description: "IoT and RFID for real-time linen inventory across facilities.",
  },
  {
    href: "/enterprise-programme",
    title: "Enterprise ERP Integration",
    description: "Connect inventory and replenishment data directly to your existing ERP via Punchout.",
  },
];

const features = [
  {
    title: '1. Tag, register & grant access controls to inventory & users',
    description:
      "Keep track of everyone's salaries and whether or not they've been paid. Direct deposit not supported.",
    image: "/images/inventory-management-solution/tag-register-rfid.jpg",
  },
  {
    title: '2. Deploy smart cabinets or check in & out systems',
    description:
      "All of your receipts organized into one place, as long as you don't mind typing in the data by hand.",
    image: "/images/inventory-management-solution/deploy-smart-cabinets.jpg",
  },
  {
    title: '3. Track & manage transactions and generate audit reports all in one place',
    description:
      "We only sell our software to companies who don't deal with VAT at all, so technically we do all the VAT stuff they need.",
    image: "/images/inventory-management-solution/track-transactions.jpg",
  },
]

const featureDescriptions = [
  {
    name: 'MF UHF RFID Tag & Inventory Assignment ',
    description:
      'Choose from a variety of RFID labels and tags, which you can quickly register and assign in the MF platform. Assign tags by location, department, or employee to streamline tracking. RFID tags ensure every item is accounted for, tracked, and traced as soon as it’s withdrawn from inventory. This setup provides full visibility and control over your assets, allowing for efficient, real-time inventory management.',
  },
  {
    name: 'RFID Cabinets & Check-Out Bins',
    description:
      'Smart cabinets & check-in or out bins track deposits and withdrawals by users. Real-time tracking provides & informs operations/logistics of inventory levels. Notifications can be sent when par levels are below minimum levels. MF system notifies you when stock levels fall below minimum thresholds and automates replenishment orders for manager approval and supplier dispatch. This results in reduced manual stock checks, ensuring accurate tracking and efficient inventory management.',
  },
  {
    name: 'MF Cloud Solution',
    description:
      'The MF Cloud Solution connects all RFID systems to the cloud for centralized control. It enables assigning user access and roles, providing a comprehensive view of inventory data. The platform generates usage and billing reports, offering insights into inventory usage. Enterprise clients can utilize onboard AI plugins for advanced analytics and deeper operational insights.',
  }
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}


export default function PageBody() {

  let [tabOrientation, setTabOrientation] = useState('horizontal')

  useEffect(() => {
    let lgMediaQuery = window.matchMedia('(min-width: 1024px)')

    function onMediaQueryChange({ matches }) {
      setTabOrientation(matches ? 'vertical' : 'horizontal')
    }

    onMediaQueryChange(lgMediaQuery)
    lgMediaQuery.addEventListener('change', onMediaQueryChange)

    return () => {
      lgMediaQuery.removeEventListener('change', onMediaQueryChange)
    }
  })

  return (
    <>
      <Header />
      <main>
      <div className="bg-white py-24 sm:py-32 relative">
        <div className="mx-auto max-w-3xl text-center px-1">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"> RFID Inventory Management Solutions </h1>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            <b> Deploy Inventory Management Solution. </b>
            MF streamlines your inventory management by deploying IoT and RFID technology to actively track, log, and control access to workwear, consumables, and other assets in real time. Our platform ensures full visibility and efficient management across all operations.
            <Link className="text-lg font-semibold leading-6 text-blue-600" href="/contact-us"> Get Started </Link>
          </p>
          
        </div>
        {/* <Tabs/> */}
        <section
          id="features"
          aria-label="Automated, real-time textile management"
          className="relative overflow-hidden bg-white py-24 mx-auto"
        >
          <div className="relative">
            {/* <b> Deploy linen management systems. </b> */}
            <Tab.Group
              as="div"
              className="grid grid-cols-1 items-center gap-y-2 gap-x-4 sm:gap-y-6 lg:grid-cols-12 lg:pt-0"
              vertical={tabOrientation === 'vertical'}
            >
              {({ selectedIndex }) => (
                <>
                  <div className="flex overflow-x-auto pb-4 sm:mx-0 sm:overflow-visible sm:pb-0 lg:col-span-5 justify-center">
                    <div className="xl:px-24 lg:px-12 sm:px-12 px-4">
                      <h3 className="mb-5 text-3xl font-bold tracking-tight text-gray-900 sm:text-3xl">
                        Automated, real-time inventory management
                      </h3>
                      {/* <h3 className="mb-5 text-xl  sm:text-3xl font-bold text-gray-900 text-center"> Automated, real-time textile management </h3> */}
                      <Tab.List className="relative z-10 flex whitespace-normal gap-x-4 px-4 sm:mx-auto sm:px-0 lg:mx-0 lg:block lg:gap-x-0 lg:gap-y-1 lg:whitespace-normal">
                        {features.map((feature, featureIndex) => (
                          <div
                            key={feature.title}
                            className={classNames(
                              'group relative rounded-xl py-1 px-4 lg:p-6 lg:my-5',
                              selectedIndex === featureIndex
                                ? 'bg-black/10 ring-1 ring-inset ring-black/10'
                                : ' bg-black/5 hover:bg-black/10'
                            )}
                          >
                            <h3>
                              <Tab
                                className={classNames(
                                  'font-display text-sm sm:text-lg [&:not(:focus-visible)]:focus:outline-none outline-none',
                                  selectedIndex === featureIndex
                                    ? 'text-gray-900'
                                    : 'hover:text-gray-900 text-gray-400'
                                )}
                              >
                                <span className="absolute inset-0 rounded-xl" />
                                {feature.title}
                              </Tab>
                            </h3>
                          </div>
                        ))}
                      </Tab.List>
                    </div>
                  </div>
                  <Tab.Panels className="lg:col-span-7">
                    {features.map((feature) => (
                      <Tab.Panel key={feature.title} unmount={false}>
                        {/* <div className="relative sm:px-6 lg:hidden">
                          <div className="absolute -inset-x-4 top-[-6.5rem] bottom-[-4.25rem] bg-white/10 ring-1 ring-inset ring-white/10 sm:inset-x-0 sm:rounded-t-xl" />
                          <p className="relative mx-auto max-w-2xl text-base text-white sm:text-center">
                            {feature.description}
                          </p>
                        </div> */}
                        <div className="mt-10 overflow-hidden rounded-xl bg-slate-50 shadow-xl shadow-blue-900/20 sm:w-auto lg:mt-0">
                          <Image
                            className="w-full"
                            src={feature.image}
                            alt={feature.title}
                            priority
                            width={1024}
                            height={1024}
                            sizes="(min-width: 1024px) 67.8125rem, (min-width: 640px) 100vw, 45rem"
                          />
                        </div>
                      </Tab.Panel>
                    ))}
                  </Tab.Panels>
                </>
              )}
            </Tab.Group>
          </div>
        </section>
        
        <div className="bg-gray-100 py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <h3 className="mb-5 text-3xl font-bold tracking-tight text-gray-900 sm:text-3xl">
              Choose the best solution for your inventory management needs
            </h3>
            <div className="mx-auto grid max-w-2xl grid-cols-1 gap-8 overflow-hidden lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {featureDescriptions.map((item) => (
                <div key={item.name}>
                  <p className="mt-6 text-lg font-semibold leading-8 tracking-tight text-gray-900">{item.name}</p>
                  <p className="mt-2 text-base leading-7 text-gray-600">{item.description}</p>
                </div>
              ))}
            </div> 
          </div>
        </div>

        <FAQSection items={inventoryFaqs} />
        <RelatedLinks links={relatedLinks} />
        <CTA/>
      </div>
    </main>
      <Footer />
    </>
  )
}